import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * BulletinContext — tracks the count of active bulletins the current
 * user hasn't read yet, so the sidebar tab can blink red until they
 * open the board. Poll-based (30s) to keep it simple; the bulletin
 * board itself calls `refresh()` after posting/reading to update
 * immediately without waiting for the next tick.
 */
const BulletinContext = createContext({ unread: 0, refresh: () => {} });

export function BulletinProvider({ children }) {
  const { user } = useAuth();
  const [unread, setUnread] = useState(0);

  const refresh = useCallback(async () => {
    if (!user) { setUnread(0); return; }
    try {
      const { data } = await api.get("/bulletins/unread-count");
      setUnread(data.unread || 0);
    } catch {
      /* ignore — nav still renders without the badge */
    }
  }, [user]);

  useEffect(() => {
    if (!user) return undefined;
    refresh();
    const id = setInterval(refresh, 30000);
    return () => clearInterval(id);
  }, [user, refresh]);

  return (
    <BulletinContext.Provider value={{ unread, refresh }}>
      {children}
    </BulletinContext.Provider>
  );
}

export function useBulletinUnread() {
  return useContext(BulletinContext);
}
